// src/data/claimValue.js
//
// WHAT A CLAIM IS WORTH. Rulings 88 and 93, and nothing else.
//
//   RULING 88.1 (Bob, 2026-08-31): "One fact: 8 hours at time and one half at the rate of the
//   position they are contesting."
//
//   RULING 93 (Bob, 2026-09-09): "For the time being use $300 a day as a place holder. Calculate that
//   number accordingly and when the general charman on-boards, thses issues must be fulfilled before
//   his useage is permissible."
//
// So: value = claimDays x 8 x 1.5 x hourly, where hourly is the straight-time rate of the CONTESTED
// position, and where no usable sourced rate exists the hourly is $300 / 8 = $37.50 and every figure
// derived from it is labelled PLACEHOLDER - on screen, in a letter, everywhere. A placeholder figure
// may never be presented to a carrier as a contract rate (PD-4).
//
// WHAT "USABLE" MEANS, AND WHY THE BAR IS WHERE IT IS. Ruling 88.2: "Multiplying Bob's formula by a
// 2001 rate would produce a number that looks current and is not." A sourced rate is only USED when
// nothing the platform knows about has superseded it. The newest national wage round the record holds
// is the NCCC-SMART-TD-YDM 2025 agreement (task list SS162 / WO-002 return 2: ratified, 4.0% eff
// 2025-07-01, 3.75% eff 2026-07-01, ...). A sourced base dated BEFORE that round's first step has, on
// the record's own knowledge, at least one increase applied to it that the platform cannot compute.
// Such a base is REPORTED beside the placeholder as the last sourced figure, dated; it is not
// multiplied. That is the difference between ISS-22's "refuse, not estimate" and a number that lies.
//
// THE FLOOR IS A DATE, NOT AN OPINION, and it moves only when a newer round enters the record.
//
// WHAT THIS MODULE DOES NOT DO. It does not read the member's typed hourlyRateST (that field predates
// ruling 88 and is his own figure, not the platform's); it does not compute officer compensation
// (ruling 88.4); it does not estimate a rate for a unit that has none (ISS-22); and it does not know
// which BN yardmasters are on the ASR pilot - a salaried unit with no overtime - so for BN it values
// under the schedule rate and SAYS that the pilot exists (see notes on the result).
//
// PURE. No Firestore, no React. Proved by scripts\test-claim-value.mjs.

import { resolveWageRate, WAGE_BARGAINING_UNITS, WAGE_RATES } from './wageRates';
import { DEFAULT_CRAFT } from './craftTerms';   // ruling 312 phase 1 (c)

export const CLAIM_DAY_HOURS = 8;              // ruling 88.1
export const CLAIM_MULTIPLIER = 1.5;           // ruling 88.1 - time and one half
export const PLACEHOLDER_DAILY = 300;          // ruling 93 - "$300 a day as a place holder"
export const PLACEHOLDER_HOURLY = PLACEHOLDER_DAILY / CLAIM_DAY_HOURS;   // 37.50
export const PLACEHOLDER_LABEL = 'PLACEHOLDER';
// The first step of the newest national wage round in the record (WO-002 return 2, 2026-09-09).
// A sourced base effective before this date has been superseded by at least that round.
export const RATE_CURRENCY_FLOOR = '2025-07-01';

export const BASIS = Object.freeze({
  SOURCED: 'SOURCED',            // a sourced rate, effective on/after the currency floor - USED
  PLACEHOLDER: 'PLACEHOLDER',    // ruling 93 - no usable sourced rate
  REFUSED: 'REFUSED',            // the unit is not one the platform holds an agreement for
});

export const PLACEHOLDER_REASON = Object.freeze({
  NO_SOURCED_RATE: 'NO_SOURCED_RATE',        // the unit has no sourced row at all
  SOURCED_RATE_STALE: 'SOURCED_RATE_STALE',  // it has one, dated before the currency floor
  NO_RATE_FOR_POSITION: 'NO_RATE_FOR_POSITION', // unit has rows, none for this position/scope as of the date
});

function round2(n) { return Math.round(n * 100) / 100; }

function isoDate(d) {
  if (!d) return new Date().toISOString().slice(0, 10);
  const s = String(d);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : new Date().toISOString().slice(0, 10);
}

// The bargaining unit a claim belongs to. Claims carry `railroad` in the same vocabulary the wage
// table keys on (RAILROADS in agreementData.js == carrier_property in YCP_Wage_BargainingUnits.csv;
// checked 2026-09-09 for all six units).
export function claimUnit(claim) {
  const c = claim || {};
  const railroad = String(c.railroad || c.carrierProperty || '').trim();
  const craft = String(c.craft || DEFAULT_CRAFT).trim();
  const unit = WAGE_BARGAINING_UNITS.find(u => u.carrier_property === railroad && u.craft === craft)
            || WAGE_BARGAINING_UNITS.find(u => u.carrier_property === railroad)
            || null;
  return { railroad, craft, unit };
}

// The last SOURCED row for a unit, any position, any scope, any date - the figure that is reported
// beside a placeholder so a reader knows what the platform holds and how old it is.
export function lastSourcedRate(railroad) {
  const rows = WAGE_RATES.filter(r => r.carrier_property === railroad && (r.rate_status || 'sourced') === 'sourced')
    .sort((a, b) => (a.effective_date < b.effective_date ? 1 : a.effective_date > b.effective_date ? -1 : 0));
  if (!rows.length) return null;
  const r = rows[0];
  return {
    baseHourly: parseFloat(r.base_hourly), effectiveDate: r.effective_date, positionClass: r.position_class,
    scope: r.scope, sourceDoc: r.source_doc, notes: r.notes,
  };
}

// Straight-time hourly of the contested position, and where it came from.
//   { basis, hourly, reason?, rate?, lastSourced?, unit }
export function resolveClaimRate(claim, opts) {
  const o = opts || {};
  const { railroad, craft, unit } = claimUnit(claim);
  const asOf = isoDate(o.asOfDate || (claim && claim.incidentDate));
  if (!unit) {
    return { basis: BASIS.REFUSED, hourly: null, unit: null, railroad, craft,
      reason: 'The platform holds no agreement for "' + (railroad || '(no railroad on the claim)') + '" / ' + craft + '; no value is computed for a unit it does not represent.' };
  }
  // RULING 179 (2026-09-17), ISS-21. The position class is a THIRD AXIS beside carrier property and
  // scope, and it exists because CSX Addendum H carries a CHIEF YARDMASTER rule: "a distinct
  // position_class with its own calculation" (data\YCP_Wage_BargainingUnits.csv, CSX row).
  //
  // The axis was built and NOTHING EVER FED IT. Checked 2026-09-17: `contestedPosition` and
  // `positionClass` appear nowhere in src\ outside this file and wageRates.js - no intake screen asks
  // for one and no claim carries one. So this line always fell through to unit.craft, which is
  // "Yardmaster", and ISS-21's predicted failure was live: A CHIEF YARDMASTER CLAIM ON CSX WAS
  // SILENTLY VALUED AT THE YARDMASTER RATE.
  //
  // The fix is not to guess the class - it is to stop the fallback being SILENT. `stated` travels
  // with the result and valueClaim() says so in the text, so a figure that rests on an assumed
  // position class announces that it does. PD-5: a default presented as a fact is the defect.
  const statedPosition = String((claim && (claim.contestedPosition || claim.positionClass)) || '').trim();
  const positionClassStated = statedPosition !== '';
  const positionClass = positionClassStated ? statedPosition : String(unit.craft);
  const scope = String((claim && claim.wageScope) || '');
  const rate = resolveWageRate(unit.carrier_property, positionClass, scope, asOf);
  const last = lastSourcedRate(unit.carrier_property);

  if (rate.found && !rate.projected && rate.effectiveDate >= RATE_CURRENCY_FLOOR) {
    return { basis: BASIS.SOURCED, hourly: rate.baseHourly, rate, unit, railroad, craft, positionClass, positionClassStated, scope, asOf };
  }
  let reason;
  if (!last) reason = PLACEHOLDER_REASON.NO_SOURCED_RATE;
  else if (!rate.found || rate.projected) reason = (last.effectiveDate < RATE_CURRENCY_FLOOR)
    ? PLACEHOLDER_REASON.SOURCED_RATE_STALE : PLACEHOLDER_REASON.NO_RATE_FOR_POSITION;
  else reason = PLACEHOLDER_REASON.SOURCED_RATE_STALE;
  return { basis: BASIS.PLACEHOLDER, hourly: PLACEHOLDER_HOURLY, reason, rate: rate.found ? rate : null,
    lastSourced: last, unit, railroad, craft, positionClass, positionClassStated, scope, asOf };
}

// What the claim is worth. claimDays defaults to 1 - "8 hours at time and one half" is one claim-day.
//   { basis, label, hourly, perHour, perClaimDay, claimDays, total, placeholder, reason?, lastSourced?, unit, text }
export function valueClaim(claim, opts) {
  const o = opts || {};
  const c = claim || {};
  // A8: the span of a continuing claim is not recorded anywhere on this platform. `claimDays` is
  // written by no screen. So `days` is an ASSUMPTION of one unless a caller states otherwise, and
  // `claimDaysStated` carries that fact out with the figure rather than leaving the reader to guess.
  const daysStated = (Number.isFinite(Number(o.claimDays)) && Number(o.claimDays) > 0) ||
                     (Number.isFinite(Number(c.claimDays)) && Number(c.claimDays) > 0);
  const days = Number.isFinite(Number(o.claimDays)) && Number(o.claimDays) > 0 ? Number(o.claimDays)
             : Number.isFinite(Number(c.claimDays)) && Number(c.claimDays) > 0 ? Number(c.claimDays)
             : 1;
  const r = resolveClaimRate(claim, o);
  if (r.basis === BASIS.REFUSED) {
    return { basis: r.basis, label: 'NOT VALUED', hourly: null, perHour: null, perClaimDay: null, claimDays: days, claimDaysStated: daysStated,
      total: null, placeholder: false, reason: r.reason, unit: null, railroad: r.railroad, craft: r.craft,
      text: 'Not valued: ' + r.reason };
  }
  const perHour = round2(r.hourly * CLAIM_MULTIPLIER);
  const perClaimDay = round2(r.hourly * CLAIM_MULTIPLIER * CLAIM_DAY_HOURS);
  const total = round2(perClaimDay * days);
  const placeholder = r.basis === BASIS.PLACEHOLDER;
  const label = placeholder ? PLACEHOLDER_LABEL : 'SOURCED';
  const money = (n) => '$' + n.toFixed(2);
  let text = money(total) + ' - ' + days + ' claim-day' + (days === 1 ? '' : 's') + ' x ' + CLAIM_DAY_HOURS +
    ' hours x ' + CLAIM_MULTIPLIER + ' x ' + money(r.hourly) + '/hr';
  if (placeholder) {
    text += ' (' + PLACEHOLDER_LABEL + ' - $' + PLACEHOLDER_DAILY + '/day, ruling 93; not a contract rate)';
    if (r.lastSourced) {
      text += '. Last sourced base on file: ' + money(r.lastSourced.baseHourly) + '/hr eff ' + r.lastSourced.effectiveDate +
        (r.lastSourced.scope ? ' (' + r.lastSourced.scope + ')' : '') + ' - superseded, not used.';
    } else {
      text += '. No sourced rate on file for this unit.';
    }
  } else {
    text += ' (sourced: ' + r.rate.sourceDoc + ', eff ' + r.rate.effectiveDate + ')';
  }
  // RULING 179 (ISS-21): a figure resting on an ASSUMED position class says so, every time it is
  // shown. The silent fallback was the defect, not the fallback itself - a claim that does not name
  // the contested position still has to be valued, but nobody may read that figure without being
  // told what class it rests on.
  // A8, and the same rule as the position class below it: an assumed span says so, every time.
  if (!daysStated) {
    text += '. Claim-days not recorded on this claim; valued as ONE claim-day. A continuing ' +
            'violation may be worth many - the span is not on record and is not assumed here beyond one.';
  }
  if (!r.positionClassStated) {
    text += '. Position class not stated on the claim; valued as ' + r.positionClass +
            ' (ISS-21: CSX Addendum H carries a distinct Chief Yardmaster class with its own calculation).';
  }
  return {
    basis: r.basis, label, hourly: r.hourly, perHour, perClaimDay, claimDays: days, total, placeholder,
    claimDaysStated: daysStated,
    positionClassStated: r.positionClassStated,
    reason: r.reason || null, lastSourced: r.lastSourced || null, rate: r.rate || null,
    unit: r.unit, railroad: r.railroad, craft: r.craft, positionClass: r.positionClass, scope: r.scope, asOf: r.asOf,
    text,
  };
}

// Every recognised unit and whether a claim on it today would be valued from a sourced rate or the
// placeholder. This is the list ruling 93 gates General Chairman onboarding on: a chairman whose units
// carry a placeholder is not enabled until the schedules are on file.
export function placeholderUnits(asOfDate) {
  return WAGE_BARGAINING_UNITS.map(u => {
    const r = resolveClaimRate({ railroad: u.carrier_property, craft: u.craft }, { asOfDate });
    return { carrierProperty: u.carrier_property, craft: u.craft, committee: u.committee,
      basis: r.basis, reason: r.reason || null,
      lastSourcedEffective: r.lastSourced ? r.lastSourced.effectiveDate : (r.basis === BASIS.SOURCED ? r.rate.effectiveDate : null) };
  });
}

// The gate itself: may a General Chairman of `committee` be enabled? Ruling 93.1.
export function gcWageGate(committee, asOfDate) {
  const units = placeholderUnits(asOfDate).filter(u => u.committee === committee);
  const blocking = units.filter(u => u.basis !== BASIS.SOURCED);
  return {
    committee, units, blocking, allowed: units.length > 0 && blocking.length === 0,
    text: units.length === 0
      ? 'No bargaining unit is recorded for ' + committee + '.'
      : blocking.length === 0
        ? 'Every unit of ' + committee + ' has a sourced current rate.'
        : blocking.length + ' of ' + units.length + ' unit(s) of ' + committee + ' would be valued at the ' + PLACEHOLDER_LABEL +
          ' rate: ' + blocking.map(b => b.carrierProperty + ' / ' + b.craft + ' (' + b.reason + ')').join('; ') +
          '. Ruling 93: the schedules must be on file before this chairman\'s use is permitted.',
  };
}
